import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale/sv';
import enUS from 'date-fns/locale/en-US';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  Bar as RechartsBar,
  BarChart as RechartsBarChart,
} from 'recharts';
import {
  MonetizationOn,
  ShoppingCart,
  ShowChart,
  BarChart,
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  TipsAndUpdates,
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { AuthContext } from '../context/AuthContext';
import {
  fetchSalesInsights,
  ChannelSplit,
  InsightItem,
  InsightTone,
  SalesInsightsResponse,
  SalesMomentum,
  WeekdayPerformanceSummary,
  fetchPeriodComparison,
  PeriodComparisonResponse,
} from '../services/salesInsightsService';

interface DailyStat {
  saleDate: string;
  totalAmount: number;
  orderCount: number;
  source: 'online' | 'retail';
  currencyCode: string;
}

interface TopBrand {
  brandName: string;
  totalRevenue: string;
  totalUnitsSold: number;
  totalSold?: number;
  onlineUnitsSold?: number;
  retailUnitsSold?: number;
  sources?: string[];
  currencyCode?: string;
}

interface TopProduct {
  productId: number;
  productNumber: string;
  productName: string;
  brandName: string;
  totalRevenue: string;
  totalUnitsSold: number;
  totalSold?: number;
  onlineUnitsSold?: number;
  retailUnitsSold?: number;
  sources?: string[];
}

interface SalesStats {
  dailyStats: DailyStat[];
  topBrands: TopBrand[];
  topProducts: TopProduct[];
}

interface CategoryPerformance {
  mainCategoryId: number;
  mainCategoryName: string;
  subCategories: {
    categoryId: number;
    categoryName: string;
    uniqueProducts: number;
    totalSold: number;
    totalRevenue: string;
    currencyCode: string;
    hasSubCategories: boolean;
  }[];
}

interface CategoryDetails {
  categoryId: number;
  categoryName: string;
  subCategoryId: number;
  subCategoryName: string;
  uniqueProducts: number;
  totalSold: number;
  totalRevenue: string;
  currencyCode: string;
}

type SalesChannelTotals = {
  onlineSales: number;
  retailSales: number;
};

type SalesByDateEntry = {
  saleDate: string;
  totalAmount: number;
  orderCount: number;
  online: {
    totalAmount: number;
    orderCount: number;
  };
  retail: {
    totalAmount: number;
    orderCount: number;
  };
};

type ChannelYoYRecord = {
  previous: number;
  changePercent: number;
};

const svWithEnUS = {
  ...sv,
  enUS: enUS.enUS,
};

const WEEKDAY_ORDER = [
  'måndag',
  'tisdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lördag',
  'söndag',
];

const formatCurrencyValue = (value: number, currencyCode = 'SEK') =>
  Number(value ?? 0).toLocaleString('sv-SE', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const isDefined = <T,>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

// Custom tooltip komponent
const CustomTooltip = ({ active, payload, label, salesType }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const showDetailedBreakdown = salesType === 'all';

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(63, 81, 181, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          color: '#1A237E',
          maxWidth: '300px',
        }}
      >
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 'bold', color: '#1A237E' }}
        >
          {new Date(label).toLocaleDateString('sv-SE', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#3F51B5',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            mb: 1,
          }}
        >
          Total:{' '}
          {data.totalAmount.toLocaleString('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </Typography>
        {showDetailedBreakdown && (
          <>
            <Typography variant="body2" sx={{ color: '#03DAC6' }}>
              E-handel:{' '}
              {data.onlineAmount.toLocaleString('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </Typography>
            <Typography variant="body2" sx={{ color: '#CF6679' }}>
              Butik:{' '}
              {data.retailAmount.toLocaleString('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </Typography>
          </>
        )}
      </Box>
    );
  }
  return null;
};

// Skapa en custom tooltip komponent för pie charts
const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(63, 81, 181, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          color: '#1A237E',
          maxWidth: '300px',
        }}
      >
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 'bold', color: '#1A237E' }}
        >
          {payload[0].name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#3F51B5',
            fontWeight: 'bold',
            fontSize: '1.1rem',
          }}
        >
          {Number(payload[0].value).toLocaleString('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </Typography>
      </Box>
    );
  }
  return null;
};

// Lägg till en ny typ för snabbvalen
type DatePreset = {
  label: string;
  getValue: () => { start: Date; end: Date };
};

// Lägg till snabbvalsalternativen
const datePresets: DatePreset[] = [
  {
    label: 'Senaste 7 dagarna',
    getValue: () => ({
      start: new Date(new Date().setDate(new Date().getDate() - 6)),
      end: new Date(),
    }),
  },
  {
    label: 'Denna månad',
    getValue: () => ({
      start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      end: new Date(),
    }),
  },
  {
    label: 'Förra månaden',
    getValue: () => ({
      start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      end: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
    }),
  },
  {
    label: 'Detta året',
    getValue: () => ({
      start: new Date(new Date().getFullYear(), 0, 1), // 1 januari nuvarande år
      end: new Date(),
    }),
  },
];

const SalesDashboard = () => {
  // Get user permissions from AuthContext first
  const { permissions } = useContext(AuthContext);
  const theme = useTheme();
  const summaryCardStyles = {
    borderRadius: 3,
    padding: theme.spacing(3),
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: '0px 24px 45px rgba(15, 23, 42, 0.08)',
    minHeight: '100%',
  };
  const iconBubbleStyles = {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  };
  const sectionCardStyles = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.98),
    boxShadow: '0 24px 40px rgba(15, 23, 42, 0.08)',
  };
  const getToggleButtonStyles = (active: boolean) => ({
    borderRadius: '999px',
    textTransform: 'none',
    px: 2.5,
    borderColor: active
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.3),
    color: active
      ? theme.palette.primary.contrastText
      : theme.palette.text.secondary,
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: active
        ? theme.palette.primary.dark
        : alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.primary.main,
    },
  });

  // Check if user has permission to view retail sales data
  const hasRetailPermission = permissions.includes('manage_prices');

  // Behåll alla hooks i början av komponenten
  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date;
  });
  const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());
  const [stats, setStats] = useState<SalesStats | null>(null);
  const [categoryStats, setCategoryStats] = useState<CategoryPerformance[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categoryDetails, setCategoryDetails] = useState<CategoryDetails[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brandSearch, setBrandSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [salesType, setSalesType] = useState<'all' | 'online' | 'retail'>(
    hasRetailPermission ? 'all' : 'online',
  );
  const [brandSortBy, setBrandSortBy] = useState<'revenue' | 'units'>(
    'revenue',
  );
  const [insightsFromApi, setInsightsFromApi] =
    useState<SalesInsightsResponse | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [comparisonData, setComparisonData] =
    useState<PeriodComparisonResponse | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);

  // If user doesn't have retail permission, force online-only view
  useEffect(() => {
    if (!hasRetailPermission && salesType !== 'online') {
      setSalesType('online');
    }
  }, [hasRetailPermission, salesType]);

  // Debounce productSearch for API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductSearch(productSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [productSearch]);

  // Memoized values
  const filteredBrands = useMemo(() => {
    if (!brandSearch.trim() || !stats?.topBrands) return stats?.topBrands;
    return stats.topBrands.filter((brand) =>
      brand.brandName.toLowerCase().includes(brandSearch.toLowerCase()),
    );
  }, [stats?.topBrands, brandSearch]);

  // Backend hanterar nu sökningen via search-parametern
  const filteredProducts = stats?.topProducts;

  const chartData = useMemo(() => {
    if (!stats?.dailyStats) return [];

    // Skapa en Map med alla unika datum
    const dateMap = new Map();

    stats.dailyStats.forEach((stat) => {
      if (!dateMap.has(stat.saleDate)) {
        dateMap.set(stat.saleDate, {
          saleDate: stat.saleDate,
          totalAmount: 0,
          onlineAmount: 0,
          retailAmount: 0,
        });
      }

      const entry = dateMap.get(stat.saleDate);
      if (stat.source === 'online') {
        entry.onlineAmount += Number(stat.totalAmount);
      } else {
        entry.retailAmount += Number(stat.totalAmount);
      }
      entry.totalAmount = entry.onlineAmount + entry.retailAmount;
    });

    // Konvertera Map till array och sortera efter datum
    return Array.from(dateMap.values()).sort(
      (a, b) => new Date(a.saleDate).getTime() - new Date(b.saleDate).getTime(),
    );
  }, [stats?.dailyStats]);

  const handleStartDateChange = (date: Date | null) => {
    console.log('handleStartDateChange:', date);
    if (date) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    console.log('handleEndDateChange:', date);
    if (date) {
      setEndDate(date);
    }
  };

  const fetchStats = async () => {
    console.log('fetchStats körs med:', {
      startDate,
      endDate,
      salesType,
      token: localStorage.getItem('token'),
    });

    try {
      if (!startDate || !endDate) {
        setError('Vänligen välj både start- och slutdatum');
        return;
      }

      setIsLoading(true);
      setError(null);

      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      console.log('Gör API-anrop till:', `${config.API_URL}/sales/stats`);

      // If user doesn't have retail permission, force salesType to 'online'
      const effectiveSalesType = hasRetailPermission ? salesType : 'online';

      const response = await axios.get(`${config.API_URL}/sales/stats`, {
        params: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          salesType: effectiveSalesType,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      // Detaljerad loggning av API-svaret
      console.log('Fullständigt API-svar:', response.data);
      console.log('Försäljningsdata:', response.data.salesData);
      console.log('Varumärkesdata:', response.data.brandPerformance);
      console.log('Produktdata:', response.data.topProducts);

      // Logga formateringen
      const formattedStats = {
        dailyStats: response.data.salesData.map((item: any) => ({
          saleDate: item.saleDate.split('T')[0],
          totalAmount: item.totalAmount,
          orderCount: item.orderCount,
          source: item.source,
          currencyCode: item.currencyCode,
        })),
        topBrands:
          response.data.brandPerformance?.map((brand: any) => ({
            brandName: brand.brandName,
            totalUnitsSold: parseInt(brand.totalUnitsSold, 10),
            totalRevenue: (
              parseFloat(brand.avgPricePerUnit) *
              parseInt(brand.totalUnitsSold, 10)
            ).toString(),
            currencyCode: brand.currencyCode,
            sources: brand.sources || [],
          })) || [],
        topProducts:
          response.data.topProducts?.map((product: any) => ({
            ...product,
            totalUnitsSold: Number(product.totalUnitsSold) || 0,
            sources: product.sources || [],
          })) || [],
      };

      console.log('Formaterad statistik:', formattedStats);

      setStats(formattedStats);
    } catch (error) {
      console.error('Ett fel uppstod i fetchStats:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.error ||
          'Ett fel uppstod vid hämtning av statistik';
        setError(errorMessage);

        // Visa felmeddelande för användaren
        if (error.response?.status === 500) {
          setError(
            'Ett serverfel uppstod. Vänligen försök igen senare eller kontakta support om problemet kvarstår.',
          );
        }
      } else {
        setError('Ett oväntat fel uppstod');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoryStats = async () => {
    try {
      if (!startDate || !endDate) {
        console.warn('Datum saknas för kategorihämtning');
        return;
      }

      // If user doesn't have retail permission, force salesType to 'online'
      const effectiveSalesType = hasRetailPermission ? salesType : 'online';

      const response = await axios.get(
        `${config.API_URL}/sales/extended-stats`,
        {
          params: {
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
            salesType: effectiveSalesType,
          },
        },
      );
      console.log('Kategoristatistik:', response.data);
      setCategoryStats(response.data.categoryPerformance);
    } catch (error) {
      console.error('Fel vid hämtning av kategoristatistik:', error);
      if (axios.isAxiosError(error)) {
        console.error('API-fel detaljer:', error.response?.data);
      }
    }
  };

  const fetchCategoryDetails = async (categoryId: number) => {
    try {
      // If user doesn't have retail permission, force salesType to 'online'
      const effectiveSalesType = hasRetailPermission ? salesType : 'online';

      const response = await axios.get(
        `${config.API_URL}/sales/category-details/${categoryId}`,
        {
          params: {
            startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
            endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
            salesType: effectiveSalesType,
          },
        },
      );
      console.log('Kategoridetaljer:', response.data);
      setCategoryDetails(response.data.categoryDetails);
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Fel vid hämtning av kategoridetaljer:', error);
      if (axios.isAxiosError(error)) {
        console.error('API-fel detaljer:', error.response?.data);
      }
    }
  };

  const fetchTopPerformers = async () => {
    try {
      if (!startDate || !endDate) return;

      // If user doesn't have retail permission, force salesType to 'online'
      const effectiveSalesType = hasRetailPermission ? salesType : 'online';

      const response = await axios.get(
        `${config.API_URL}/sales/top-performers`,
        {
          params: {
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
            salesType: effectiveSalesType,
            limit: 100,
            sortBy: brandSortBy,
            ...(debouncedProductSearch.trim() && {
              search: debouncedProductSearch.trim(),
            }),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        },
      );

      console.log('Top performers data:', response.data);

      setStats((prevStats) => {
        if (!prevStats) return null;
        return {
          ...prevStats,
          topBrands: response.data.topBrands,
          topProducts: response.data.topProducts,
        };
      });
    } catch (error) {
      console.error('Fel vid hämtning av top performers:', error);
      if (axios.isAxiosError(error)) {
        setError(
          error.response?.data?.error ||
            'Ett fel uppstod vid hämtning av statistik',
        );
      }
    }
  };

  useEffect(() => {
    console.log('useEffect för fetchStats körs med:', {
      startDate,
      endDate,
      salesType,
    });

    if (startDate && endDate) {
      fetchStats();
      fetchTopPerformers();
    }
  }, [startDate, endDate, salesType, brandSortBy]);

  // Separat useEffect för sökning - undviker att hela sidan laddas om
  useEffect(() => {
    if (startDate && endDate) {
      fetchTopPerformers();
    }
  }, [debouncedProductSearch]);

  useEffect(() => {
    // Lägg till kontroll innan anrop
    if (startDate && endDate) {
      fetchCategoryStats();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const loadInsights = async () => {
      if (!startDate || !endDate) return;
      setInsightsLoading(true);
      setInsightsError(null);
      try {
        const response = await fetchSalesInsights(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd'),
        );
        setInsightsFromApi(response);
      } catch (err) {
        console.error('Kunde inte hämta fördjupad statistik:', err);
        setInsightsError(
          'Fördjupade insikter kunde inte hämtas just nu. Visar beräkningar från klienten.',
        );
      } finally {
        setInsightsLoading(false);
      }
    };

    loadInsights();
  }, [startDate, endDate]);

  useEffect(() => {
    const loadComparison = async () => {
      if (!startDate || !endDate) return;
      setComparisonLoading(true);
      setComparisonError(null);
      try {
        const response = await fetchPeriodComparison(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd'),
        );
        setComparisonData(response);
      } catch (err) {
        console.error('Kunde inte hämta jämförelsedata:', err);
        setComparisonError(
          'Kunde inte hämta jämförelse med föregående period/år.',
        );
      } finally {
        setComparisonLoading(false);
      }
    };

    loadComparison();
  }, [startDate, endDate]);

  // Uppdatera beräkningarna för statistiken
  const calculateStats = () => {
    const emptyChannelTotals: SalesChannelTotals = {
      onlineSales: 0,
      retailSales: 0,
    };

    if (!stats?.dailyStats?.length) {
      return {
        totalSales: 0,
        totalOrders: 0,
        averageOrderValue: 0,
        bestDay: {
          saleDate: '',
          totalAmount: 0,
          orderCount: 0,
          source: 'online',
          currencyCode: 'SEK',
        },
        dailyStats: [],
        channelTotals: emptyChannelTotals,
      };
    }

    const salesByDate = stats.dailyStats.reduce(
      (acc: Record<string, SalesByDateEntry>, curr) => {
        const date = curr.saleDate;
        if (!acc[date]) {
          acc[date] = {
            saleDate: date,
            totalAmount: 0,
            orderCount: 0,
            online: { totalAmount: 0, orderCount: 0 },
            retail: { totalAmount: 0, orderCount: 0 },
          };
        }

        if (curr.source === 'online') {
          acc[date].online.totalAmount += Number(curr.totalAmount);
          acc[date].online.orderCount += Number(curr.orderCount);
        } else {
          acc[date].retail.totalAmount += Number(curr.totalAmount);
          acc[date].retail.orderCount += Number(curr.orderCount);
        }

        acc[date].totalAmount += Number(curr.totalAmount);
        acc[date].orderCount += Number(curr.orderCount);
        return acc;
      },
      {} as Record<string, SalesByDateEntry>,
    );

    const dailyStats = Object.values(salesByDate);

    const channelTotals = dailyStats.reduce<SalesChannelTotals>(
      (acc, day) => {
        acc.onlineSales += day.online.totalAmount;
        acc.retailSales += day.retail.totalAmount;
        return acc;
      },
      { ...emptyChannelTotals },
    );

    // Beräkna totaler
    const totalSales = dailyStats.reduce(
      (sum, day) => sum + day.totalAmount,
      0,
    );
    const totalOrders = dailyStats.reduce(
      (sum, day) => sum + day.orderCount,
      0,
    );
    const averageOrderValue = totalOrders ? totalSales / totalOrders : 0;

    // Hitta bästa försäljningsdagen med null-check
    const bestDay =
      dailyStats.length > 0
        ? dailyStats.reduce(
            (best, current) =>
              current.totalAmount > (best?.totalAmount || 0) ? current : best,
            dailyStats[0],
          )
        : {
            saleDate: '',
            totalAmount: 0,
            orderCount: 0,
            source: 'online',
            currencyCode: 'SEK',
          };

    return {
      totalSales,
      totalOrders,
      averageOrderValue,
      bestDay,
      dailyStats,
      channelTotals,
    };
  };

  const statsData = calculateStats();

  const sortedDailyStats = useMemo(() => {
    if (!statsData?.dailyStats?.length) return [];
    return [...statsData.dailyStats].sort(
      (a, b) => new Date(a.saleDate).getTime() - new Date(b.saleDate).getTime(),
    );
  }, [statsData?.dailyStats]);

  const salesMomentum = useMemo<SalesMomentum | null>(() => {
    if (!sortedDailyStats.length) return null;
    const comparisonWindow = Math.min(
      3,
      Math.floor(sortedDailyStats.length / 2),
    );

    if (comparisonWindow < 1) return null;

    const lastWindow = sortedDailyStats.slice(-comparisonWindow);
    const previousWindow = sortedDailyStats.slice(
      -comparisonWindow * 2,
      -comparisonWindow,
    );

    if (!previousWindow.length) return null;

    const average = (list: SalesByDateEntry[]) =>
      list.reduce((sum, day) => sum + day.totalAmount, 0) / list.length;

    const lastAverage = average(lastWindow);
    const prevAverage = average(previousWindow);

    if (!prevAverage) {
      return {
        changePercent: 0,
        trend: 'flat',
        lastAverage,
        prevAverage,
        window: comparisonWindow,
      };
    }

    const changePercent = ((lastAverage - prevAverage) / prevAverage) * 100;

    return {
      changePercent,
      trend: changePercent > 5 ? 'up' : changePercent < -5 ? 'down' : 'flat',
      lastAverage,
      prevAverage,
      window: comparisonWindow,
    };
  }, [sortedDailyStats]);

  const channelDistribution = useMemo<ChannelSplit[] | null>(() => {
    if (!statsData?.channelTotals) return null;
    const { onlineSales, retailSales } = statsData.channelTotals;
    const total = onlineSales + retailSales;
    if (!total) return null;

    return [
      {
        label: 'E-handel',
        value: onlineSales,
        percent: Math.round((onlineSales / total) * 100),
        color: '#03DAC6',
      },
      {
        label: 'Butik',
        value: retailSales,
        percent: Math.round((retailSales / total) * 100),
        color: '#CF6679',
      },
    ];
  }, [statsData?.channelTotals]);

  // Beräkna försäljning per land
  const salesByCountry = stats?.dailyStats.reduce(
    (acc: { [key: string]: number }, curr) => {
      const country = curr.currencyCode === 'SEK' ? 'Sverige' : 'Danmark';
      acc[country] = (acc[country] || 0) + Number(curr.totalAmount);
      return acc;
    },
    {},
  );

  // Formatera data för cirkeldiagrammet
  const pieChartData = salesByCountry
    ? Object.entries(salesByCountry).map(([country, amount]) => ({
        name: country,
        value: amount,
      }))
    : [];

  // Uppdatera COLORS arrayen för fler nyanser
  const COLORS = [
    '#BB86FC',
    '#03DAC6',
    '#CF6679',
    '#985EFF',
    '#FFB86C',
    '#FF79C6',
    '#50FA7B',
    '#8BE9FD',
    '#BD93F9',
    '#FF5555',
  ];
  const formatUnits = (value?: number) => {
    return (value ?? 0).toLocaleString('sv-SE');
  };
  const getChannelBreakdownText = (
    onlineUnits?: number,
    retailUnits?: number,
    totalUnits?: number,
    sources?: string[],
  ) => {
    const parts: string[] = [];
    const showOnline =
      (sources?.includes('online') ?? false) || (onlineUnits ?? 0) > 0;
    const showRetail =
      (sources?.includes('retail') ?? false) || (retailUnits ?? 0) > 0;

    if (showOnline) {
      parts.push(`Online: ${formatUnits(onlineUnits)} st`);
    }

    if (showRetail) {
      parts.push(`Butik: ${formatUnits(retailUnits)} st`);
    }

    if (!parts.length) {
      return null;
    }

    const totalValue =
      totalUnits !== undefined
        ? totalUnits
        : (onlineUnits ?? 0) + (retailUnits ?? 0);

    parts.push(`Totalt: ${formatUnits(totalValue)} st`);

    return `Antal sålda: ${parts.join(' • ')}`;
  };

  // Beräkna ny statistik
  const calculateExtendedStats = () => {
    if (!stats?.dailyStats.length) return null;

    const numberOfDays = stats.dailyStats.length;
    const dailyAverage = statsData?.totalSales
      ? statsData.totalSales / numberOfDays
      : 0;

    // Hitta bästa försäljningsdagen
    const bestDay = [...stats.dailyStats].sort(
      (a, b) => Number(b.totalAmount) - Number(a.totalAmount),
    )[0];

    // Beräkna försäljning per veckodag
    const salesByWeekday = stats.dailyStats.reduce(
      (acc: { [key: string]: number }, curr) => {
        const weekday = new Date(curr.saleDate).toLocaleDateString('sv-SE', {
          weekday: 'long',
        });
        acc[weekday] = (acc[weekday] || 0) + Number(curr.totalAmount);
        return acc;
      },
      {},
    );

    return {
      dailyAverage,
      bestDay,
      salesByWeekday,
    };
  };

  const extendedStats = calculateExtendedStats();

  const weekdayPerformance = useMemo<WeekdayPerformanceSummary | null>(() => {
    if (!extendedStats?.salesByWeekday) return null;

    const entries = Object.entries(
      extendedStats.salesByWeekday as Record<string, number>,
    ).map(([weekday, value]) => ({
      weekday,
      value,
    }));

    if (!entries.length) return null;

    const sortedEntries = [...entries].sort((a, b) => b.value - a.value);
    const best = sortedEntries[0];
    const worst = sortedEntries[sortedEntries.length - 1];

    const normalizedEntries = WEEKDAY_ORDER.map((weekdayKey) => {
      const match = sortedEntries.find(
        (entry) => entry.weekday.toLowerCase() === weekdayKey,
      );
      return match
        ? {
            weekday: match.weekday,
            value: match.value,
            percent: best.value
              ? Math.round((match.value / best.value) * 100)
              : 0,
          }
        : null;
    }).filter(isDefined);

    return {
      entries:
        normalizedEntries.length > 0
          ? normalizedEntries
          : sortedEntries.map((entry) => ({
              ...entry,
              percent: best.value
                ? Math.round((entry.value / best.value) * 100)
                : 0,
            })),
      best,
      worst,
    };
  }, [extendedStats?.salesByWeekday]);

  const clientInsights = useMemo<InsightItem[]>(() => {
    if (!statsData?.dailyStats?.length) return [];

    const insightsList: InsightItem[] = [];

    if (salesMomentum && Math.abs(salesMomentum.changePercent) >= 2) {
      const toneMap: Record<SalesMomentum['trend'], InsightTone> = {
        up: 'positive',
        down: 'warning',
        flat: 'info',
      };

      const changeText = `${salesMomentum.changePercent > 0 ? '+' : ''}${salesMomentum.changePercent.toFixed(
        1,
      )}%`;
      insightsList.push({
        id: 'momentum',
        title:
          salesMomentum.trend === 'down'
            ? `Farten bromsar ${changeText}`
            : `Farten ökar ${changeText}`,
        description: `Snittet senaste ${salesMomentum.window} dagarna är ${formatCurrencyValue(
          salesMomentum.lastAverage,
        )} jämfört med ${formatCurrencyValue(
          salesMomentum.prevAverage,
        )} föregående period.`,
        action:
          salesMomentum.trend === 'down'
            ? 'Planera en snabb kampanj eller prisjustering innan trenden sätter sig.'
            : 'Fyll på lager och synka marknadsföringen för att rida på uppgången.',
        tone: toneMap[salesMomentum.trend],
      });
    }

    if (weekdayPerformance?.best && weekdayPerformance?.worst) {
      const diff =
        weekdayPerformance.best.value - weekdayPerformance.worst.value;
      const diffPercent = weekdayPerformance.best.value
        ? Math.round((diff / weekdayPerformance.best.value) * 100)
        : 0;

      insightsList.push({
        id: 'weekday-focus',
        title: `${weekdayPerformance.best.weekday} är hetaste dagen`,
        description: `${weekdayPerformance.best.weekday} står för ${formatCurrencyValue(
          weekdayPerformance.best.value,
        )} denna period.`,
        action:
          diffPercent > 20
            ? `Testa riktade aktiviteter mot ${weekdayPerformance.worst.weekday} för att ta igen ${diffPercent}% gapet.`
            : 'Förstärk bästa dagen med utskick kvällen innan för att bibehålla trycket.',
        tone: diffPercent > 25 ? 'warning' : 'info',
      });
    }

    if (channelDistribution && hasRetailPermission && salesType === 'all') {
      const online = channelDistribution.find(
        (item) => item.label === 'E-handel',
      );
      const retail = channelDistribution.find((item) => item.label === 'Butik');

      if (online && retail) {
        if (online.percent >= 70) {
          insightsList.push({
            id: 'channel-online-dominance',
            title: 'E-handeln dominerar mixen',
            description: `Online står för ${online.percent}% (${formatCurrencyValue(
              online.value,
            )}) av omsättningen.`,
            action:
              'Planera butikskampanjer eller click & collect för att balansera.',
            tone: 'warning',
          });
        } else if (retail.percent >= 60) {
          insightsList.push({
            id: 'channel-retail-dominance',
            title: 'Butiksförsäljningen leder stort',
            description: `Butik står för ${retail.percent}% (${formatCurrencyValue(
              retail.value,
            )}) av omsättningen.`,
            action:
              'Se över digital synlighet för att inte tappa online-trafiken.',
            tone: 'info',
          });
        } else {
          insightsList.push({
            id: 'channel-balanced',
            title: 'Balans mellan kanalerna',
            description: `Fördelningen online/butik är ${online.percent}% / ${retail.percent}%.`,
            action: 'Kör gemensamma kampanjer för att hålla mixen stabil.',
            tone: 'positive',
          });
        }
      }
    }

    if (sortedDailyStats.length) {
      const weakestDay = sortedDailyStats.reduce((prev, curr) =>
        curr.totalAmount < prev.totalAmount ? curr : prev,
      );
      const averageDayValue = sortedDailyStats.length
        ? statsData.totalSales / sortedDailyStats.length
        : 0;

      if (averageDayValue && weakestDay.totalAmount < averageDayValue * 0.85) {
        const weekday = new Date(weakestDay.saleDate).toLocaleDateString(
          'sv-SE',
          { weekday: 'long' },
        );
        const dropPercent = Math.round(
          ((averageDayValue - weakestDay.totalAmount) / averageDayValue) * 100,
        );

        insightsList.push({
          id: 'weak-day',
          title: `${weekday} tappar ${dropPercent}% mot snittet`,
          description: `${new Date(weakestDay.saleDate).toLocaleDateString(
            'sv-SE',
          )} landade på ${formatCurrencyValue(weakestDay.totalAmount)}.`,
          action:
            'Planera push dagen innan (sms/nyhetsbrev) för att lyfta nivån.',
          tone: 'info',
        });
      }
    }

    if (stats?.topBrands?.length) {
      const totalBrandRevenue = stats.topBrands.reduce(
        (sum, brand) => sum + Number(brand.totalRevenue ?? 0),
        0,
      );
      const leadingBrand = stats.topBrands[0];
      const leadingShare = totalBrandRevenue
        ? Math.round(
            (Number(leadingBrand.totalRevenue ?? 0) / totalBrandRevenue) * 100,
          )
        : 0;

      insightsList.push({
        id: 'brand-focus',
        title: `${leadingBrand.brandName} står för ${leadingShare}% av toppintäkterna`,
        description: `Genererar ${formatCurrencyValue(
          Number(leadingBrand.totalRevenue ?? 0),
        )} under vald period.`,
        action:
          leadingShare > 45
            ? 'Sprid risk: exponera #2-3 i sortimentet i nästa kampanj.'
            : 'Skapa highlight med varumärket i sociala kanaler medan trenden är stark.',
        tone: leadingShare > 45 ? 'warning' : 'positive',
      });
    }

    if (
      hasRetailPermission &&
      salesType === 'all' &&
      stats?.topProducts?.length
    ) {
      const onlineOnlyProduct = stats.topProducts.find(
        (product) =>
          (product.onlineUnitsSold ?? product.totalUnitsSold ?? 0) > 0 &&
          (product.retailUnitsSold ?? 0) === 0,
      );

      if (onlineOnlyProduct) {
        insightsList.push({
          id: `omnichannel-${onlineOnlyProduct.productId}`,
          title: `${onlineOnlyProduct.productName} säljer endast online`,
          description: `${formatUnits(
            onlineOnlyProduct.totalUnitsSold,
          )} st för ${formatCurrencyValue(
            Number(onlineOnlyProduct.totalRevenue ?? 0),
          )}.`,
          action:
            'Testa pilot i 1-2 butiker eller erbjud hämtning samma dag för att sprida framgången.',
          tone: 'info',
        });
      }
    }

    return insightsList.slice(0, 6);
  }, [
    statsData,
    salesMomentum,
    weekdayPerformance,
    channelDistribution,
    stats?.topBrands,
    stats?.topProducts,
    sortedDailyStats,
    hasRetailPermission,
    salesType,
  ]);

  const insightToneStyles: Record<
    InsightTone,
    {
      chipColor:
        | 'default'
        | 'primary'
        | 'secondary'
        | 'error'
        | 'info'
        | 'success'
        | 'warning';
      borderColor: string;
    }
  > = {
    positive: {
      chipColor: 'success',
      borderColor: 'rgba(3, 218, 198, 0.4)',
    },
    warning: {
      chipColor: 'warning',
      borderColor: 'rgba(207, 102, 121, 0.45)',
    },
    info: {
      chipColor: 'info',
      borderColor: 'rgba(187, 134, 252, 0.4)',
    },
  };

  const effectiveMomentum = insightsFromApi?.momentum ?? salesMomentum;
  const effectiveChannelDistribution =
    insightsFromApi?.channelMix && insightsFromApi.channelMix.length
      ? insightsFromApi.channelMix
      : channelDistribution;
  const effectiveWeekdayPerformance =
    insightsFromApi?.weekdayPerformance ?? weekdayPerformance;
  const effectiveInsights =
    insightsFromApi?.insights && insightsFromApi.insights.length
      ? insightsFromApi.insights
      : clientInsights;
  const apiSummary = insightsFromApi?.summary;
  const yoyComparison = useMemo(() => {
    if (!comparisonData?.yearOverYear) return null;
    const previousTotal = comparisonData.yearOverYear.totalSales;
    const currentTotal =
      comparisonData.current?.totalSales ?? statsData?.totalSales ?? null;
    if (!previousTotal || !currentTotal) return null;
    const changePercent =
      ((currentTotal - previousTotal) / previousTotal) * 100;
    return {
      previousTotal,
      changePercent,
    };
  }, [comparisonData, statsData?.totalSales]);
  const yoyChannelComparison = useMemo<{
    online: ChannelYoYRecord | null;
    retail: ChannelYoYRecord | null;
  } | null>(() => {
    if (
      !comparisonData?.yearOverYear ||
      !statsData?.channelTotals ||
      salesType !== 'all'
    ) {
      return null;
    }

    const { onlineSales: prevOnline, retailSales: prevRetail } =
      comparisonData.yearOverYear;
    const { onlineSales: currentOnline, retailSales: currentRetail } =
      statsData.channelTotals;

    const buildRecord = (
      currentValue: number,
      previousValue: number | undefined,
    ): ChannelYoYRecord | null => {
      if (!previousValue || previousValue === 0) return null;
      return {
        previous: previousValue,
        changePercent: ((currentValue - previousValue) / previousValue) * 100,
      };
    };

    return {
      online: buildRecord(currentOnline, prevOnline),
      retail: buildRecord(currentRetail, prevRetail),
    };
  }, [comparisonData, statsData?.channelTotals, salesType]);
  const channelScopeText = useMemo(() => {
    if (salesType === 'online') {
      return 'Visar endast e-handelsförsäljning för vald period.';
    }
    if (salesType === 'retail') {
      return 'Visar endast butiksförsäljning för vald period.';
    }
    return 'Visar totalsiffror för både e-handel och butik.';
  }, [salesType]);
  const renderChannelCard = (
    label: string,
    value: number,
    yoyRecord: ChannelYoYRecord | null | undefined,
  ) => (
    <Box
      sx={{
        borderRadius: 2,
        p: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        minHeight: 108,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle1">
        {value.toLocaleString('sv-SE', {
          style: 'currency',
          currency: 'SEK',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
      </Typography>
      {yoyRecord ? (
        <>
          <Chip
            size="small"
            label={`${yoyRecord.changePercent > 0 ? '+' : ''}${yoyRecord.changePercent.toFixed(
              1,
            )}% vs föreg. år`}
            color={yoyRecord.changePercent >= 0 ? 'success' : 'warning'}
            sx={{ alignSelf: 'flex-start', fontSize: '0.75rem' }}
          />
          <Typography variant="caption" color="text.secondary">
            Föregående år: {formatCurrencyValue(yoyRecord.previous)}
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {comparisonLoading
            ? 'Läser in föregående år...'
            : 'Ingen föregående årsdata'}
        </Typography>
      )}
    </Box>
  );

  // Lägg till denna hjälpfunktion för att formatera data för pie charts
  const formatCategoryData = (category: CategoryPerformance) => {
    return category.subCategories
      .map((sub) => ({
        name: sub.categoryName,
        value: parseFloat(sub.totalRevenue),
        categoryId: sub.categoryId,
        hasSubCategories: sub.hasSubCategories,
      }))
      .filter((item) => !isNaN(item.value) && item.value > 0);
  };

  // Lägg till efter datumväljaren
  const salesTypeButtons = (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mb: 2,
        flexWrap: 'wrap',
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        borderRadius: '999px',
        padding: '6px',
      }}
    >
      {hasRetailPermission && (
        <Button
          variant={salesType === 'all' ? 'contained' : 'outlined'}
          onClick={() => setSalesType('all')}
          size="small"
          sx={getToggleButtonStyles(salesType === 'all')}
        >
          Alla försäljningar
        </Button>
      )}
      <Button
        variant={salesType === 'online' ? 'contained' : 'outlined'}
        onClick={() => setSalesType('online')}
        size="small"
        sx={getToggleButtonStyles(salesType === 'online')}
      >
        E-handel
      </Button>
      {hasRetailPermission && (
        <Button
          variant={salesType === 'retail' ? 'contained' : 'outlined'}
          onClick={() => setSalesType('retail')}
          size="small"
          sx={getToggleButtonStyles(salesType === 'retail')}
        >
          Fysisk butik
        </Button>
      )}
    </Box>
  );

  if (isLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 2, lg: 6 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: 'rgba(255, 50, 50, 0.1)',
            color: '#ff6b6b',
          }}
        >
          {error}
        </Alert>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            mt: 4,
            mb: 4,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: 4,
            alignItems: 'center',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            boxShadow: '0 24px 45px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Försäljningsstatistik
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: theme.palette.text.secondary,
                fontSize: '0.95rem',
              }}
            >
              Statistiken uppdateras dagligen kl. 02:00. Alla belopp är
              inklusive moms.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {channelScopeText}
            </Typography>
            {startDate && endDate && (
              <Chip
                label={`${format(startDate, 'd MMM', { locale: sv })} – ${format(
                  endDate,
                  'd MMM yyyy',
                  { locale: sv },
                )}`}
                sx={{
                  mt: 2,
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  fontWeight: 600,
                }}
              />
            )}
          </Box>

          <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                color: theme.palette.text.primary,
                textAlign: { xs: 'left', md: 'right' },
              }}
            >
              Välj period
            </Typography>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 3,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => date && setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <TextField
                      size="small"
                      label="Från datum"
                      sx={{ width: 150 }}
                    />
                  }
                />
                <DatePicker
                  selected={endDate}
                  onChange={(date) => date && setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <TextField
                      size="small"
                      label="Till datum"
                      sx={{ width: 150 }}
                    />
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {datePresets.map((preset) => (
                  <Button
                    key={preset.label}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      console.log('Snabbval klickat:', preset.label);
                      const { start, end } = preset.getValue();
                      setStartDate(start);
                      setEndDate(end);
                    }}
                    sx={{
                      borderRadius: '999px',
                      textTransform: 'none',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08,
                        ),
                      },
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {salesTypeButtons}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.2 }}>
            <Card sx={summaryCardStyles}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Total försäljning
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {statsData?.totalSales.toLocaleString('sv-SE', {
                        style: 'currency',
                        currency: 'SEK',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </Box>
                  <Box sx={iconBubbleStyles}>
                    <MonetizationOn sx={{ fontSize: 26 }} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Inklusive moms för vald period.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {channelScopeText}
                </Typography>
                {salesType === 'all' && statsData?.channelTotals && (
                  <Box
                    sx={{
                      mt: 2,
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.06,
                      )}`,
                      p: 2,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Kanalfördelning (nuvarande period)
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(auto-fit, minmax(240px, 1fr))',
                        },
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      {renderChannelCard(
                        'E-handel',
                        statsData.channelTotals.onlineSales,
                        yoyChannelComparison?.online,
                      )}
                      {renderChannelCard(
                        'Butik',
                        statsData.channelTotals.retailSales,
                        yoyChannelComparison?.retail,
                      )}
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  {comparisonLoading ? (
                    <Typography variant="caption" color="text.secondary">
                      Läser in föregående år...
                    </Typography>
                  ) : yoyComparison ? (
                    <>
                      <Chip
                        size="small"
                        label={`${yoyComparison.changePercent > 0 ? '+' : ''}${yoyComparison.changePercent.toFixed(
                          1,
                        )}% vs föregående år`}
                        color={
                          yoyComparison.changePercent >= 0
                            ? 'success'
                            : 'warning'
                        }
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Föregående år:{' '}
                        {formatCurrencyValue(yoyComparison.previousTotal)}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="caption"
                      color={comparisonError ? 'error' : 'text.secondary'}
                    >
                      {comparisonError ||
                        'Föregående års data saknas för vald period.'}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.2 }}>
            <Card sx={summaryCardStyles}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Antal ordrar
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {statsData?.totalOrders}
                    </Typography>
                  </Box>
                  <Box sx={iconBubbleStyles}>
                    <ShoppingCart sx={{ fontSize: 26 }} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Inkluderar både online- och butiksförsäljning.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {channelScopeText}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.2 }}>
            <Card sx={summaryCardStyles}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Snittorder (inkl. moms)
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {statsData?.averageOrderValue.toLocaleString('sv-SE', {
                        style: 'currency',
                        currency: 'SEK',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </Box>
                  <Box sx={iconBubbleStyles}>
                    <ShowChart sx={{ fontSize: 26 }} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Beräknat på samtliga ordrar under perioden.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {channelScopeText}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.2 }}>
            <Card sx={summaryCardStyles}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Bästa försäljningsdagen
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {statsData?.bestDay?.totalAmount
                        ? statsData.bestDay.totalAmount.toLocaleString(
                            'sv-SE',
                            {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            },
                          )
                        : '0 kr'}
                    </Typography>
                  </Box>
                  <Box sx={iconBubbleStyles}>
                    <EmojiEvents sx={{ fontSize: 26 }} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {statsData?.bestDay?.saleDate
                    ? new Date(statsData.bestDay.saleDate).toLocaleDateString(
                        'sv-SE',
                        { month: 'short', day: 'numeric' },
                      )
                    : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {channelScopeText}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.2 }}>
            <Card sx={summaryCardStyles}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Dagligt genomsnitt
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {extendedStats?.dailyAverage.toLocaleString('sv-SE', {
                        style: 'currency',
                        currency: 'SEK',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  </Box>
                  <Box sx={iconBubbleStyles}>
                    <BarChart sx={{ fontSize: 26 }} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Genomsnittligt värde per dag för vald period.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {channelScopeText}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardStyles, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Försäljningspuls</Typography>
                {effectiveMomentum?.trend === 'down' ? (
                  <TrendingDown sx={{ color: '#CF6679' }} />
                ) : (
                  <TrendingUp sx={{ color: '#03DAC6' }} />
                )}
              </Box>
              {effectiveMomentum ? (
                <>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color:
                        effectiveMomentum.trend === 'down'
                          ? '#CF6679'
                          : '#03DAC6',
                    }}
                  >
                    {effectiveMomentum.changePercent > 0 ? '+' : ''}
                    {effectiveMomentum.changePercent.toFixed(1)}%
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Jämfört med föregående {effectiveMomentum.window} dagar.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Nytt snitt:{' '}
                    {formatCurrencyValue(effectiveMomentum.lastAverage)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tidigare snitt:{' '}
                    {formatCurrencyValue(effectiveMomentum.prevAverage)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Välj minst 4 dagar för att se trend.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardStyles, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Kanalmix</Typography>
                <Lightbulb sx={{ color: '#FFB74D' }} />
              </Box>
              {effectiveChannelDistribution ? (
                effectiveChannelDistribution.map((channel) => (
                  <Box key={channel.label} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2">{channel.label}</Typography>
                      <Typography variant="body2">
                        {channel.percent}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={channel.percent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mt: 0.5,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: channel.color,
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrencyValue(channel.value)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Ingen mix att visa för vald vy.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardStyles, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6">Veckodag att fokusera</Typography>
                <BarChart sx={{ color: '#BB86FC' }} />
              </Box>
              {effectiveWeekdayPerformance ? (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 1 }}>
                    Starkast: {effectiveWeekdayPerformance.best.weekday}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrencyValue(
                      effectiveWeekdayPerformance.best.value,
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Svagast: {effectiveWeekdayPerformance.worst.weekday}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrencyValue(
                      effectiveWeekdayPerformance.worst.value,
                    )}
                  </Typography>
                  <Box sx={{ height: 160, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart
                        data={effectiveWeekdayPerformance.entries}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="weekday"
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            formatCurrencyValue(Number(value))
                          }
                        />
                        <RechartsBar
                          dataKey="value"
                          fill="#BB86FC"
                          radius={[8, 8, 0, 0]}
                        />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Ingen veckodataprofils hittades.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={sectionCardStyles}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6">Insikter & nästa steg</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Genererade utifrån vald period och kanalmix.
                  </Typography>
                </Box>
                <TipsAndUpdates sx={{ color: '#FFB74D' }} />
              </Box>
              <Grid container spacing={2}>
                {insightsError && (
                  <Grid item xs={12}>
                    <Alert severity="warning">{insightsError}</Alert>
                  </Grid>
                )}
                {insightsLoading && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Hämtar fördjupade insikter...
                    </Typography>
                  </Grid>
                )}
                {effectiveInsights.length > 0 ? (
                  effectiveInsights.map((insight) => (
                    <Grid item xs={12} md={4} key={insight.id}>
                      <Box
                        sx={{
                          borderRadius: 2,
                          p: 2,
                          height: '100%',
                          border: `1px solid ${insightToneStyles[insight.tone].borderColor}`,
                          backgroundColor: 'rgba(0, 0, 0, 0.15)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Chip
                          size="small"
                          label={
                            insight.tone === 'positive'
                              ? 'Momentum'
                              : insight.tone === 'warning'
                                ? 'Varning'
                                : 'Tips'
                          }
                          color={insightToneStyles[insight.tone].chipColor}
                          sx={{ alignSelf: 'flex-start' }}
                        />
                        <Typography variant="subtitle1">
                          {insight.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {insight.description}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 'auto', fontWeight: 600, color: '#03DAC6' }}
                        >
                          {insight.action}
                        </Typography>
                      </Box>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {insightsLoading
                        ? 'Läser in...'
                        : 'Välj ett längre intervall för att generera rekommendationer.'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {apiSummary && (
          <Grid item xs={12} md={4}>
            <Card sx={{ ...sectionCardStyles, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Kundbeteende & marginal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Period {apiSummary.startDate} – {apiSummary.endDate}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Återkommande kunder
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#03DAC6' }}>
                    {(apiSummary.returningCustomerRate * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Bruttomarginal
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#BB86FC' }}>
                    {(apiSummary.grossMarginRate * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Card sx={{ ...sectionCardStyles, height: '400px' }}>
            <CardContent sx={{ p: 1, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Försäljningsutveckling (inkl. moms)
              </Typography>
              <Box sx={{ height: 'calc(100% - 32px)' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{
                      top: 0,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="saleDate"
                      tickFormatter={(date) =>
                        new Date(date).toLocaleDateString('sv-SE', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        value
                          .toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                          .replace('kr', '')
                          .trim()
                      }
                    />
                    <Tooltip
                      content={(props) => (
                        <CustomTooltip {...props} salesType={salesType} />
                      )}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalAmount"
                      stroke="url(#colorGradient)"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#BB86FC' }}
                      activeDot={{ r: 8, fill: '#03DAC6' }}
                    />
                    <defs>
                      <linearGradient
                        id="colorGradient"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="0"
                      >
                        <stop offset="0%" stopColor="#BB86FC" />
                        <stop offset="100%" stopColor="#03DAC6" />
                      </linearGradient>
                    </defs>
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ ...sectionCardStyles, height: '400px' }}>
            <CardContent sx={{ p: 1, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Försäljning per land (inkl. moms)
              </Typography>
              <Box
                sx={{
                  height: 'calc(100% - 32px)',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({
                        cx,
                        cy,
                        midAngle,
                        innerRadius,
                        outerRadius,
                        percent,
                        name,
                        value,
                      }) => {
                        const RADIAN = Math.PI / 180;
                        const radius =
                          innerRadius + (outerRadius - innerRadius) * 0.5;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);

                        return (
                          <text
                            x={x}
                            y={y}
                            fill="#333333"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                          >
                            {`${name} ${(percent * 100).toFixed(0)}%`}
                          </text>
                        );
                      }}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) =>
                        value.toLocaleString('sv-SE', {
                          style: 'currency',
                          currency: 'SEK',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{
              mt: 3,
              mb: 3,
            }}
          >
            Försäljning per kategori
          </Typography>
          <Grid container spacing={3}>
            {categoryStats.map((category) => {
              const pieData = formatCategoryData(category);
              const totalValue = pieData.reduce(
                (sum, item) => sum + item.value,
                0,
              );

              return (
                <Grid item xs={12} md={4} key={category.mainCategoryId}>
                  <Card
                    sx={{
                      height: '400px',
                      background:
                        'linear-gradient(135deg, rgba(187, 134, 252, 0.1), rgba(3, 218, 198, 0.1))',
                      backdropFilter: 'blur(10px)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                      '& .recharts-sector:focus': {
                        outline: 'none',
                      },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {category.mainCategoryName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Totalt:{' '}
                        {totalValue.toLocaleString('sv-SE', {
                          style: 'currency',
                          currency: 'SEK',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </Typography>
                      <Box sx={{ height: 300, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={2}
                              dataKey="value"
                              nameKey="name"
                              onClick={(data) => {
                                if (data && data.hasSubCategories) {
                                  fetchCategoryDetails(data.categoryId);
                                }
                              }}
                              label={({
                                cx,
                                cy,
                                midAngle,
                                innerRadius,
                                outerRadius,
                                percent,
                                value,
                                name,
                              }) => {
                                // Visa endast label för kategorier som utgör mer än 5% av totalen
                                if (percent < 0.05) return null;

                                const RADIAN = Math.PI / 180;
                                // Beräkna position för labeln
                                const radius = outerRadius + 20;
                                const x =
                                  cx + radius * Math.cos(-midAngle * RADIAN);
                                const y =
                                  cy + radius * Math.sin(-midAngle * RADIAN);

                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    fill="#333333"
                                    textAnchor={x > cx ? 'start' : 'end'}
                                    dominantBaseline="central"
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      filter:
                                        'drop-shadow(0px 0px 2px rgba(255, 255, 255, 0.7))',
                                    }}
                                  >
                                    {name}
                                  </text>
                                );
                              }}
                              labelLine={true}
                            >
                              {pieData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                  style={{
                                    cursor: entry.hasSubCategories
                                      ? 'pointer'
                                      : 'default',
                                  }}
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomPieTooltip />} />
                            <Legend
                              verticalAlign="bottom"
                              height={36}
                              formatter={(value, entry) => {
                                const { payload } = entry as any;
                                const percentage = (
                                  (payload.value / totalValue) *
                                  100
                                ).toFixed(1);
                                return `${value} (${percentage}%)`;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '400px',
                  background:
                    'linear-gradient(135deg, rgba(187, 134, 252, 0.1), rgba(3, 218, 198, 0.1))',
                }}
              >
                <CardContent sx={{ height: '100%', p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Populäraste varumärken
                  </Typography>

                  {/* Sökfält och filtreringsknappar */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={brandSearch}
                      onChange={(e) => setBrandSearch(e.target.value)}
                      placeholder="Sök varumärke..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: brandSearch && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault(); // Förhindra scroll
                                setBrandSearch('');
                              }}
                              sx={{ color: 'text.secondary' }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(187, 134, 252, 0.3)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#BB86FC',
                          },
                        },
                      }}
                    />

                    {/* Filtreringsknappar */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant={
                          brandSortBy === 'revenue' ? 'contained' : 'outlined'
                        }
                        onClick={(e) => {
                          e.preventDefault(); // Förhindra scroll
                          setBrandSortBy('revenue');
                        }}
                        size="small"
                        sx={{
                          borderColor: 'rgba(187, 134, 252, 0.3)',
                          '&.MuiButton-contained': {
                            backgroundColor: '#BB86FC',
                            '&:hover': {
                              backgroundColor: '#9F66FF',
                            },
                          },
                        }}
                      >
                        Försäljningsvärde
                      </Button>
                      <Button
                        variant={
                          brandSortBy === 'units' ? 'contained' : 'outlined'
                        }
                        onClick={(e) => {
                          e.preventDefault(); // Förhindra scroll
                          setBrandSortBy('units');
                        }}
                        size="small"
                        sx={{
                          borderColor: 'rgba(187, 134, 252, 0.3)',
                          '&.MuiButton-contained': {
                            backgroundColor: '#BB86FC',
                            '&:hover': {
                              backgroundColor: '#9F66FF',
                            },
                          },
                        }}
                      >
                        Antal sålda
                      </Button>
                    </Box>
                  </Box>

                  {/* Lista med varumärken */}
                  <Box
                    sx={{
                      height: 'calc(100% - 110px)', // Justerad höjd för att ge plats åt sökfältet
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(187, 134, 252, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'rgba(187, 134, 252, 0.5)',
                        },
                      },
                    }}
                  >
                    {filteredBrands?.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                          color: 'text.secondary',
                        }}
                      >
                        <Typography>Inga varumärken hittades</Typography>
                      </Box>
                    ) : (
                      filteredBrands?.map((brand, index) => {
                        const totalBrandUnits =
                          brand.totalUnitsSold ?? brand.totalSold ?? 0;
                        const brandBreakdown =
                          salesType === 'all'
                            ? getChannelBreakdownText(
                                brand.onlineUnitsSold,
                                brand.retailUnitsSold,
                                totalBrandUnits,
                                brand.sources,
                              )
                            : null;

                        return (
                          <Box
                            key={`${brand.brandName}-${index}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                              p: 2,
                              borderRadius: 1,
                              background: `linear-gradient(90deg, ${COLORS[index % COLORS.length]}20 0%, transparent 100%)`,
                              '&:hover': {
                                background: `linear-gradient(90deg, ${COLORS[index % COLORS.length]}40 0%, transparent 100%)`,
                              },
                            }}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1">
                                {index + 1}. {brand.brandName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {brandBreakdown ||
                                  `Antal sålda: ${formatUnits(totalBrandUnits)} st`}
                                {!brandBreakdown &&
                                  brand.sources &&
                                  brand.sources.length > 0 && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        fontSize: '0.8rem',
                                        opacity: 0.7,
                                      }}
                                    >
                                      ({brand.sources.join(', ')})
                                    </Typography>
                                  )}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: COLORS[index % COLORS.length],
                                  fontWeight: 'bold',
                                }}
                              >
                                {Number(brand.totalRevenue).toLocaleString(
                                  'sv-SE',
                                  {
                                    style: 'currency',
                                    currency: 'SEK',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  },
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '400px',
                  background:
                    'linear-gradient(135deg, rgba(3, 218, 198, 0.1), rgba(207, 102, 121, 0.1))',
                }}
              >
                <CardContent sx={{ height: '100%', p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Populäraste produkter
                  </Typography>

                  {/* Sökfält för produkter */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Sök artikelnummer, namn eller varumärke..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: productSearch && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                setProductSearch('');
                              }}
                              sx={{ color: 'text.secondary' }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(3, 218, 198, 0.3)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#03DAC6',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      height: 'calc(100% - 90px)',
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(3, 218, 198, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: 'rgba(3, 218, 198, 0.5)',
                        },
                      },
                    }}
                  >
                    {filteredProducts?.map((product, index) => {
                      const totalProductUnits =
                        product.totalUnitsSold ?? product.totalSold ?? 0;
                      const productBreakdown =
                        salesType === 'all'
                          ? getChannelBreakdownText(
                              product.onlineUnitsSold,
                              product.retailUnitsSold,
                              totalProductUnits,
                              product.sources,
                            )
                          : null;

                      return (
                        <Box
                          key={`${product.productNumber}-${index}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            background: `linear-gradient(90deg, ${COLORS[index % COLORS.length]}20 0%, transparent 100%)`,
                            '&:hover': {
                              background: `linear-gradient(90deg, ${COLORS[index % COLORS.length]}40 0%, transparent 100%)`,
                            },
                          }}
                        >
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">
                              {index + 1}. {product.productName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: 'rgba(3, 218, 198, 0.8)',
                                fontFamily: 'monospace',
                                mb: 0.5,
                              }}
                            >
                              Art.nr: {product.productNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.brandName} •{' '}
                              {productBreakdown ||
                                `Antal sålda: ${formatUnits(totalProductUnits)} st`}
                              {!productBreakdown &&
                                product.sources &&
                                product.sources.length > 0 && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 1,
                                      fontSize: '0.8rem',
                                      opacity: 0.7,
                                    }}
                                  >
                                    ({product.sources.join(', ')})
                                  </Typography>
                                )}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: COLORS[index % COLORS.length],
                                fontWeight: 'bold',
                              }}
                            >
                              {Number(product.totalRevenue).toLocaleString(
                                'sv-SE',
                                {
                                  style: 'currency',
                                  currency: 'SEK',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                },
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 3 }}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ordrar per dag och land
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '500px',
                  overflowY: 'auto',
                }}
              >
                {/* Rubrikrad */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    gap: 2,
                    p: 2,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                  }}
                >
                  <Typography variant="subtitle2">Datum</Typography>
                  <Typography variant="subtitle2">Sverige</Typography>
                  <Typography variant="subtitle2">Danmark</Typography>
                  <Typography variant="subtitle2">Totalt</Typography>
                </Box>

                {/* Data rader */}
                {stats &&
                  Object.values(
                    stats.dailyStats.reduce(
                      (acc: { [key: string]: any }, curr) => {
                        const date = curr.saleDate;
                        if (!acc[date]) {
                          acc[date] = {
                            date,
                            SE: { orders: 0, amount: 0 },
                            DK: { orders: 0, amount: 0 },
                          };
                        }
                        const country =
                          curr.currencyCode === 'SEK' ? 'SE' : 'DK';
                        acc[date][country].orders += Number(curr.orderCount);
                        acc[date][country].amount += Number(curr.totalAmount);
                        return acc;
                      },
                      {} as { [key: string]: any },
                    ),
                  )
                    .sort(
                      (a: any, b: any) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime(),
                    )
                    .map((dayData: any) => (
                      <Box
                        key={dayData.date}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr 1fr',
                          gap: 2,
                          p: 2,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          '&:hover': {
                            bgcolor: 'rgba(187, 134, 252, 0.1)',
                          },
                        }}
                      >
                        <Typography>
                          {new Date(dayData.date).toLocaleDateString('sv-SE', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Typography>

                        <Box>
                          <Typography sx={{ color: '#03DAC6' }}>
                            {Math.round(dayData.SE.orders)} ordrar
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#BB86FC' }}>
                            {dayData.SE.amount.toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography sx={{ color: '#03DAC6' }}>
                            {Math.round(dayData.DK.orders)} ordrar
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#BB86FC' }}>
                            {dayData.DK.amount.toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography sx={{ color: '#03DAC6' }}>
                            {Math.round(dayData.SE.orders + dayData.DK.orders)}{' '}
                            ordrar
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#BB86FC' }}>
                            {(
                              dayData.SE.amount + dayData.DK.amount
                            ).toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Underkategorier</DialogTitle>
          <DialogContent>
            {categoryDetails.length > 0 && (
              <Box
                sx={{
                  height: 400,
                  position: 'relative',
                  '& .recharts-sector:focus': {
                    outline: 'none',
                  },
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryDetails.map((detail) => ({
                        name: detail.subCategoryName,
                        value: parseFloat(detail.totalRevenue),
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({
                        cx,
                        cy,
                        midAngle,
                        innerRadius,
                        outerRadius,
                        percent,
                        value,
                        name,
                      }) => {
                        // Visa endast label för kategorier som utgör mer än 5% av totalen
                        if (percent < 0.05) return null;

                        const RADIAN = Math.PI / 180;
                        // Beräkna position för labeln
                        const radius = outerRadius + 20;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);

                        return (
                          <text
                            x={x}
                            y={y}
                            fill="#333333"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{
                              fontSize: '12px',
                              fontWeight: 500,
                              filter:
                                'drop-shadow(0px 0px 2px rgba(255, 255, 255, 0.7))',
                            }}
                          >
                            {name}
                          </text>
                        );
                      }}
                      labelLine={true}
                    >
                      {categoryDetails.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Stäng</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Container>
  );
};

export default SalesDashboard;
